import { z } from "zod";

/**
 * Parameter types — UI hints for the card editor.
 * The actual value is always a string at the API level.
 */
export const PARAMETER_TYPES = [
  "string",
  "text",
  "email",
  "phone",
  "number",
  "boolean",
  "url",
  "object",
  "array"
] as const;

export const ParameterTypeSchema = z.enum(PARAMETER_TYPES);
export type ParameterType = z.infer<typeof ParameterTypeSchema>;

/**
 * Describes one parameter of a service action.
 * Used by the card editor UI to render appropriate inputs.
 */
export const ParameterDefinitionSchema = z.object({
  name: z.string(),
  type: ParameterTypeSchema,
  required: z.boolean(),
  description: z.string()
});

export type ParameterDefinition = z.infer<typeof ParameterDefinitionSchema>;

/**
 * Pipedream component types — "action" performs an operation, "source" emits events.
 * Used to group actions in the card editor UI.
 */
export const COMPONENT_TYPES = ["action", "source"] as const;
export const ComponentTypeSchema = z.enum(COMPONENT_TYPES);
export type ComponentType = z.infer<typeof ComponentTypeSchema>;

/**
 * Describes one action a service supports.
 * The `actionId` uses the `{service}:{action}` format (e.g. "gmail:send_email").
 */
export const ActionDefinitionSchema = z.object({
  actionId: z.string(),
  name: z.string(),
  description: z.string(),
  componentType: ComponentTypeSchema.optional(),
  parameters: z.array(ParameterDefinitionSchema)
});

export type ActionDefinition = z.infer<typeof ActionDefinitionSchema>;

/**
 * All actions available for a single service.
 * The `serviceKey` is the lowercase slug used as action ID prefix.
 */
export const ServiceActionCatalogSchema = z.object({
  serviceKey: z.string(),
  name: z.string(),
  description: z.string(),
  actions: z.array(ActionDefinitionSchema)
});

export type ServiceActionCatalog = z.infer<typeof ServiceActionCatalogSchema>;
